import { Cancel01Icon } from '@hugeicons/core-free-icons';
import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { Button, ButtonVariants } from '../components/Button';
import { GET_HEADER_HEIGHT } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { TextBold } from '../components/StyledText';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import type { WithResponsive } from '../utils/responsivity';
import { useMediaQueries } from '../utils/responsivity';
import { useSafeContext } from './useSafeContext';

export type BannerAction = {
  label: string;
  onPress: () => void | Promise<void>;
};

export type BannerState = {
  text: string;
  autoDismiss?: boolean;
  isBlocking?: boolean;
  action?: BannerAction;
};

type BannerContextType = {
  banner: BannerState | undefined;
  showBanner: (banner: BannerState) => void;
  hideBanner: () => void;
};

// @ts-expect-error -- don't use default, instead console error if component not used in appropriate context
export const BannerContext = createContext<BannerContextType>();
BannerContext.displayName = 'BannerContext';

type Props = {
  children: ReactNode;
};

export const BannerProvider = ({ children }: Props) => {
  const [banner, setBanner] = useState<BannerState | undefined>();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();
  const { top } = useSafeAreaInsets();
  const startOffset = -100 - top;
  const translateY = useSharedValue(startOffset);

  useEffect(() => {
    if (isVisible) {
      translateY.value = withSpring(0, {
        damping: 100,
        stiffness: 100,
      });
    } else {
      translateY.value = withTiming(
        startOffset,
        { duration: 400 },
        (finished) => {
          if (finished) {
            runOnJS(setBanner)(undefined);
          }
        },
      );
    }
  }, [isVisible, translateY, top]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const hideBanner = useCallback(() => {
    setIsVisible(false);
  }, []);

  const showBanner = useCallback(
    (newBanner: BannerState) => {
      setBanner(newBanner);
      setIsVisible(true);
      if (!newBanner.isBlocking && newBanner.autoDismiss) {
        setTimeout(hideBanner, 3500);
      }
    },
    [hideBanner],
  );

  return (
    <BannerContext.Provider value={{ banner, showBanner, hideBanner }}>
      <View style={styles.container}>
        {children}
        <Animated.View style={[styles.banner, animatedStyle]}>
          <SafeAreaView edges={['top']} style={{ width: '100%' }}>
            <View style={styles.bannerContent}>
              <TextBold style={styles.text}>{banner?.text}</TextBold>
              <View style={styles.actionsContainer}>
                {banner?.action && (
                  <Button
                    onPress={banner.action.onPress}
                    variant={ButtonVariants.YELLOW}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    <TextBold style={styles.buttonText}>
                      {banner.action.label}
                    </TextBold>
                  </Button>
                )}
                {!banner?.isBlocking && (
                  <IconButton
                    icon={Cancel01Icon}
                    onPress={hideBanner}
                    size={isMobile ? 'small' : 'medium'}
                    backgroundColor="transparent"
                  />
                )}
              </View>
            </View>
          </SafeAreaView>
        </Animated.View>
      </View>
    </BannerContext.Provider>
  );
};

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      position: 'relative',
    },
    banner: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      backgroundColor: Colors.pink0,
    },
    bannerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      height: GET_HEADER_HEIGHT(isMobile) + 4,
      paddingHorizontal: isMobile ? 12 : 16,
      gap: 12,
      maxWidth: 960,
      alignSelf: 'center',
    },
    text: {
      fontSize: isMobile ? 12 : 16,
    },
    buttonText: {
      color: Colors.purple0,
    },
    actionsContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
  });

export const useBanner = () => {
  return useSafeContext(BannerContext);
};
